import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import Select, { StylesConfig } from 'react-select';
import { UseFormMethods } from 'react-hook-form';
import useFetch from 'use-http';
import { useInfiniteScroll, IPagedResult } from 'src/util/useInfiniteScroll';

interface ICityOption {
    value: string; // city name
    label: string; // city name (no separate code, unlike Country)
}

interface ICitySelectProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'setValue'>> {
    name: string;
    defaultCity?: string;
    isDisabled?: boolean;
    style?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    onCityChange?: (city: string) => void;
}

// Debounced search-as-you-type City picker, backed by the distinct City values already in
// LMS.dbo.ZipCode (there's no independent "City" master list anywhere in DLCTSDI/LMS - City only
// ever exists as a plain column, most structured on ZipCode) via /v1.0/usps/cities. Paging reuses
// the shared useInfiniteScroll hook, same as CountrySelect.tsx this was modeled on.
const CitySelect = (props: ICitySelectProps) => {
    const { get, response } = useFetch();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedCity, setSelectedCity] = useState<string | undefined>(props.defaultCity);
    const [defaultOption, setDefaultOption] = useState<ICityOption | undefined>(
        props.defaultCity ? { value: props.defaultCity, label: props.defaultCity } : undefined
    );

    const fetchPage = async (pageNumber: number): Promise<IPagedResult<ICityOption>> => {
        const result = await get(
            `/v1.0/usps/cities?search=${encodeURIComponent(searchTerm)}&pageNumber=${pageNumber}`
        );

        if (!response.ok || !result) {
            return { items: [], totalRecords: 0 };
        }

        return {
            items: (result.items ?? []).map((city: string) => ({ value: city, label: city })),
            totalRecords: result.totalRecords ?? 0,
        };
    };

    const { items: options, hasMore, loadNextPage, reset } = useInfiniteScroll<ICityOption>(fetchPage, 25);

    useEffect(() => {
        setSelectedCity(props.defaultCity);
        if (props.defaultCity) {
            setDefaultOption({ value: props.defaultCity, label: props.defaultCity });
        }
    }, [props.defaultCity]);

    // Fires on mount (searchTerm starts at '', loading page 1 of the unfiltered list) and again on
    // every new search term - reset() clears the accumulated pages first so loadNextPage() doesn't
    // just append the filtered results onto stale ones from the previous term.
    useEffect(() => {
        reset();
        loadNextPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const debouncedSearch = useRef(_.debounce((term: string) => setSearchTerm(term), 800)).current;

    const handleInputChange = (value: string, meta: { action: string }) => {
        if (meta.action === 'input-change') {
            debouncedSearch(value);
        }
        return value;
    };

    const handleChange = (option: ICityOption | null) => {
        setSelectedCity(option?.value);
        props.setValue?.(props.name, option?.value ?? '', { shouldDirty: true });
        props.onCityChange?.(option?.value ?? '');
    };

    const selectedOption = options.find((o) => o.value === selectedCity)
        ?? (selectedCity === defaultOption?.value ? defaultOption : null)
        ?? (selectedCity ? { value: selectedCity, label: selectedCity } : null);

    const customStyles: StylesConfig<ICityOption, false> = {
        control: (provided) => ({
            ...provided,
            minHeight: '36px',
            height: '36px',
            fontSize: '14px',
            border: 'none',
            borderRadius: 0,
            borderBottom: '1px solid grey',
            boxShadow: 'none',
            backgroundColor: 'white',
        }),
        valueContainer: (provided) => ({ ...provided, padding: '0 4px', height: '36px' }),
        input: (provided) => ({ ...provided, margin: 0, padding: 0, fontSize: '14px' }),
        singleValue: (provided) => ({ ...provided, fontSize: '14px' }),
        placeholder: (provided) => ({ ...provided, fontSize: '14px' }),
        option: (provided) => ({ ...provided, fontSize: '14px' }),
        indicatorsContainer: (provided) => ({ ...provided, height: '36px' }),
        dropdownIndicator: (provided) => ({ ...provided, padding: '0 2px' }),
        clearIndicator: (provided) => ({ ...provided, padding: '0 2px' }),
    };

    const formatOptionLabel = (option: ICityOption) => option.label;

    const handleMenuScrollToBottom = () => {
        if (hasMore) {
            loadNextPage();
        }
    };

    return (
        <div style={props.bodyStyle ?? { width: '100%' }}>
            <input type="hidden" name={props.name} ref={props.register} defaultValue={props.defaultCity} />
            <Select
                isClearable={true}
                isSearchable={true}
                isDisabled={props.isDisabled}
                options={options}
                value={selectedOption}
                onInputChange={handleInputChange}
                onChange={handleChange}
                onMenuScrollToBottom={handleMenuScrollToBottom}
                formatOptionLabel={formatOptionLabel}
                placeholder="Search Cities..."
                styles={customStyles}
            />
        </div>
    );
};

export default CitySelect;



// WEBPACK FOOTER //
// ./src/components/client-project/components/CitySelect.tsx